import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { API } from "../constants";

import useAuthStore from "../hooks/useAuthStore";
import useCartStore from "../hooks/useCartStore";
import VehicleDetailsModal from "../Component/Modal/VehicleDetailsModal";
import toast from "react-hot-toast";

const Cart = () => {
  const navigate = useNavigate();

  const { user } = useAuthStore();

  const loggedInUser = user?.user || user || null;

  const customerId = loggedInUser?._id || loggedInUser?.id || null;

  const [cartOrder, setCartOrder] = useState(null);
  const [cartLoading, setCartLoading] = useState(true);
  const [cartMutationLoading, setCartMutationLoading] = useState({});
  const [clearCartLoading, setClearCartLoading] = useState(false);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [onlineOrderLoading, setOnlineOrderLoading] = useState(false);
  const { setCartItemCount } = useCartStore();

  const safeNumber = (value) => {
    const number = Number(value || 0);

    return Number.isFinite(number) ? number : 0;
  };

  const formatMoney = (value) => {
    return `$${safeNumber(value).toFixed(2)}`;
  };

  const extractCartFromResult = (result) => {
    setCartItemCount(result.data.items.length);
    return result?.data?.cart || result?.cart || result?.data || null;
  };

  const applyCartResponse = useCallback((result) => {
    const cart = result?.data;

    if (cart && cart._id) {
      setCartOrder(cart);
      setCartItemCount(cart.items.length);

      return cart;
    }
    setCartItemCount(cart.items.length);
    setCartOrder(null);

    return null;
  }, []);

  const cartItems = useMemo(() => {
    return Array.isArray(cartOrder?.items) ? cartOrder.items : [];
  }, [cartOrder]);

  const ticketItems = useMemo(() => {
    return cartItems.filter(
      (item) => String(item.itemType || "").toLowerCase() === "ticket",
    );
  }, [cartItems]);

  const productItems = useMemo(() => {
    return cartItems.filter((item) => {
      const itemType = String(item.itemType || "").toLowerCase();

      return ["food", "product"].includes(itemType);
    });
  }, [cartItems]);

  const cartCount = useMemo(() => {
    return cartItems.reduce(
      (total, item) => total + safeNumber(item.quantity),
      0,
    );
  }, [cartItems]);

  const subtotal = safeNumber(cartOrder?.subtotal);
  const taxAmount = safeNumber(cartOrder?.taxAmount);
  const discountAmount = safeNumber(cartOrder?.discountAmount);
  const totalAmount = safeNumber(cartOrder?.totalAmount);

  const fetchCartOrder = useCallback(async () => {
    if (!customerId) {
      setCartOrder(null);
      setCartLoading(false);

      return;
    }

    try {
      setCartLoading(true);

      const url = `${
        API.FETCH_CART_ORDER
      }/${encodeURIComponent(String(customerId))}`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",

        headers: {
          Accept: "application/json",
        },
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || result?.status === false || result?.status === 0) {
        setCartOrder(null);

        return;
      }

      applyCartResponse(result);
    } catch (error) {
      setCartOrder(null);
    } finally {
      setCartLoading(false);
    }
  }, [customerId, applyCartResponse]);

  useEffect(() => {
    fetchCartOrder();
  }, [fetchCartOrder]);

  const updateCartQuantity = async (action, item) => {
    const cartId = cartOrder?._id;
    const cartItemId = item?._id;

    const currentQuantity = Math.max(safeNumber(item.quantity), 0);

    const quantity =
      action === "increment"
        ? currentQuantity + 1
        : Math.max(currentQuantity - 1, 0);

    const endpoint =
      action === "increment" ? API.INCREMENT_CART : API.DECREMENT_CART;

    const loadingKey = `${action}-${cartItemId}`;

    if (cartMutationLoading[loadingKey]) {
      return;
    }

    try {
      setCartMutationLoading((current) => ({
        ...current,

        [loadingKey]: true,
      }));

      const url =
        `${endpoint}/` +
        `${encodeURIComponent(String(customerId))}/` +
        `${encodeURIComponent(String(cartId))}/` +
        `${encodeURIComponent(String(cartItemId))}`;

      const response = await fetch(url, {
        method: "PATCH",
        credentials: "include",

        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          quantity,
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || result?.status === false || result?.status === 0) {
        toast.error(result?.message || `Unable to ${action} cart item.`);

        return;
      }

      const cart = applyCartResponse(result);

      if (!cart) {
        await fetchCartOrder();
      }
    } catch (error) {
      window.alert(`Unable to ${action} cart item.`);
    } finally {
      setCartMutationLoading((current) => {
        const updated = {
          ...current,
        };

        delete updated[loadingKey];

        return updated;
      });
    }
  };

  const handleIncrease = (item) => {
    updateCartQuantity("increment", item);
  };

  const handleDecrease = (item) => {
    updateCartQuantity("decrement", item);
  };

  const handleRemove = async (item) => {
    const cartId = cartOrder?._id;
    const cartItemId = item?._id;

    const loadingKey = `delete-${cartItemId}`;

    if (cartMutationLoading[loadingKey]) {
      return;
    }

    try {
      setCartMutationLoading((current) => ({
        ...current,

        [loadingKey]: true,
      }));

      const url =
        `${API.DELETE_CART_ITEM}/` +
        `${encodeURIComponent(String(customerId))}/` +
        `${encodeURIComponent(String(cartId))}/` +
        `${encodeURIComponent(String(cartItemId))}`;

      const response = await fetch(url, {
        method: "DELETE",
        credentials: "include",

        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || result?.status === false || result?.status === 0) {
        toast.error(result?.message || "Unable to remove cart item.");

        return;
      }

      const cart = applyCartResponse(result);

      if (!cart) {
        await fetchCartOrder();
      }
    } catch (error) {
      toast.error("Unable to remove cart item.");
    } finally {
      setCartMutationLoading((current) => {
        const updated = {
          ...current,
        };

        delete updated[loadingKey];

        return updated;
      });
    }
  };

  const clearCart = async () => {
    if (!customerId) {
      return;
    }

    if (clearCartLoading) {
      return;
    }

    try {
      setClearCartLoading(true);

      const url = `${API.CLEAR_CART}/${encodeURIComponent(String(customerId))}`;

      const response = await fetch(url, {
        method: "DELETE",
        credentials: "include",

        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || result?.status === false || result?.status === 0) {
        toast.error(result?.message || "Unable to clear cart.");

        return;
      }

      const cart = extractCartFromResult(result);

      if (cart?._id && Array.isArray(cart.items)) {
        setCartOrder(cart);
      } else {
        setCartOrder(null);
      }
    } catch (error) {
      toast.error("Unable to clear cart.");
    } finally {
      setClearCartLoading(false);
    }
  };

  const getItemName = (item) => {
    return (
      item.itemName ||
      item.productName ||
      item.ticketName ||
      item.booking?.ticketType ||
      item.movieName ||
      item.name ||
      "Item"
    );
  };

  const getLineSubtotal = (item) => {
    return safeNumber(
      item.subtotal ??
        safeNumber(item.unitPrice) * safeNumber(item.quantity || 1),
    );
  };

  const getLineTotal = (item) => {
    return safeNumber(item.totalAmount ?? getLineSubtotal(item));
  };

  const ticketTotal = useMemo(() => {
    return ticketItems.reduce(
      (total, item) => total + getLineSubtotal(item),
      0,
    );
  }, [ticketItems]);

  const foodTotal = useMemo(() => {
    return productItems.reduce(
      (total, item) => total + getLineSubtotal(item),
      0,
    );
  }, [productItems]);

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      return;
    }

    if (!customerId) {
      navigate("/login");
      return;
    }

    setVehicleModalOpen(true);
  };

  const handleCreateOnlineOrder = async (vehicleDetails) => {
    if (!customerId) {
      navigate("/login");
      return;
    }

    if (!cartOrder?._id || cartItems.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }

    if (onlineOrderLoading) {
      return;
    }

    try {
      setOnlineOrderLoading(true);

      const companyId = cartOrder?.companyId;

      if (!companyId) {
        toast.error("Company information is missing.");
        return;
      }

      const parkingDetails = {
        registrationNumber: String(vehicleDetails.registrationNumber || "")
          .trim()
          .toUpperCase(),

        make: String(vehicleDetails.make || "").trim(),
        model: String(vehicleDetails.model || "").trim(),
        color: String(vehicleDetails.color || "").trim(),
        vehicleType: String(vehicleDetails.vehicleType || "").trim(),
      };

      const customerName = cartOrder?.customerName;

      const customerPhone = cartOrder?.customerPhone;

      const customerEmail = cartOrder?.customerEmail;
      const orderPayload = {
        companyId: String(companyId),
        customerId: String(customerId),
        customerName,
        customerPhone,
        customerEmail,
        items: cartItems,
        subtotal: safeNumber(cartOrder?.subtotal),
        taxAmount: safeNumber(cartOrder?.taxAmount),
        discountAmount: safeNumber(cartOrder?.discountAmount),
        itemDiscountAmount: safeNumber(cartOrder?.itemDiscountAmount),
        orderDiscountAmount: safeNumber(cartOrder?.orderDiscountAmount),
        discountReason: cartOrder?.discountReason || null,
        totalAmount: safeNumber(cartOrder?.totalAmount),
        paymentStatus: "paid",
        bookingDate: cartOrder.bookingDate,
        bookingTime: cartOrder.bookingTime,
        parkingDetails,
        cartId: cartOrder._id,
      };

      const response = await fetch(API.CREATE_ONLINE_ORDER, {
        method: "POST",
        credentials: "include",

        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },

        body: JSON.stringify(orderPayload),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || result?.status === false || result?.status === 0) {
        toast.error(result?.message);

        return;
      }

      const createdOrder = result?.data;

      if (!createdOrder) {
        toast.error("Failed to fetch order information");
        return;
      }

      setVehicleModalOpen(false);

      toast.success(result?.message);
      setCartItemCount(0)
      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setOnlineOrderLoading(false);
    }
  };

  return (
    <main className="professional-cart-page">
      <section className="professional-cart-hero">
        <div className="container">
          <div className="professional-cart-hero-inner">
            <div>
              <div className="professional-cart-eyebrow">
                <i className="fa-solid fa-bag-shopping" />
                Your Order
              </div>

              <h1>Shopping Cart</h1>

              <p>
                Review your movie tickets, food and drinks before proceeding to
                checkout.
              </p>
            </div>

            <div className="professional-cart-count">
              <span>{cartCount}</span>

              <small>{cartCount === 1 ? "Item" : "Items"}</small>
            </div>
          </div>
        </div>
      </section>

      <div className="professional-cart-breadcrumb-wrap">
        <div className="container">
          <div className="professional-cart-breadcrumb">
            <Link to="/">Home</Link>

            <i className="fa-solid fa-chevron-right" />

            <span>Shopping Cart</span>
          </div>
        </div>
      </div>

      <section className="professional-cart-content">
        <div className="container">
          {!customerId ? (
            <div className="professional-empty-cart">
              <div className="professional-empty-cart-icon">
                <i className="fa-solid fa-user-lock" />
              </div>

              <h2>Login to view your cart</h2>

              <p>
                Your online cart is linked directly to your customer account.
              </p>

              <button
                type="button"
                className="professional-continue-btn"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
            </div>
          ) : cartLoading ? (
            <div className="professional-empty-cart">
              <div className="professional-empty-cart-icon">
                <i className="fa-solid fa-spinner fa-spin" />
              </div>

              <h2>Loading your cart</h2>

              <p>Please wait while we retrieve your items.</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="professional-empty-cart">
              <div className="professional-empty-cart-icon">
                <i className="fa-solid fa-cart-shopping" />
              </div>

              <h2>Your cart is empty</h2>

              <p>You haven't added any movie tickets or food items yet.</p>

              <Link to="/" className="professional-continue-btn">
                <i className="fa-solid fa-arrow-left" />
                Continue Shopping
              </Link>
            </div>
          ) : (
            <div className="row g-4">
              <div className="col-xl-8 col-lg-7">
                <div className="professional-cart-card">
                  <div className="professional-cart-card-header">
                    <div>
                      <h2>Your Items</h2>

                      <p>
                        {cartCount} {cartCount === 1 ? "item" : "items"} in your
                        cart
                      </p>
                    </div>

                    <button
                      type="button"
                      className="professional-clear-btn"
                      disabled={clearCartLoading}
                      onClick={clearCart}
                    >
                      {clearCartLoading ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" />
                          Clearing...
                        </>
                      ) : (
                        <>
                          <i className="fa-regular fa-trash-can" />
                          Clear Cart
                        </>
                      )}
                    </button>
                  </div>

                  <div className="professional-cart-items">
                    {cartItems.map((item, index) => {
                      const quantity = safeNumber(item.quantity || 1);

                      const unitPrice = safeNumber(item.unitPrice);

                      const lineTotal = getLineTotal(item);

                      const itemType = String(
                        item.itemType || "",
                      ).toLowerCase();

                      const isTicket = itemType === "ticket";

                      const isFood =
                        itemType === "food" || itemType === "product";

                      const incrementLoading =
                        cartMutationLoading[`increment-${item._id}`];

                      const decrementLoading =
                        cartMutationLoading[`decrement-${item._id}`];

                      const deleteLoading =
                        cartMutationLoading[`delete-${item._id}`];

                      const loading =
                        incrementLoading || decrementLoading || deleteLoading;

                      return (
                        <article
                          className="professional-cart-item"
                          key={item._id || item.itemId || index}
                        >
                          <div className="professional-cart-details">
                            <h3>{getItemName(item)}</h3>

                            {isTicket && item.booking && (
                              <div className="professional-ticket-info">
                                {item.booking.screenName && (
                                  <span>
                                    <i className="fa-solid fa-tv" />

                                    {item.booking.screenName}
                                  </span>
                                )}

                                {item.booking.date && (
                                  <span>
                                    <i className="fa-regular fa-calendar" />

                                    {item.booking.date}
                                  </span>
                                )}

                                {item.booking.time && (
                                  <span>
                                    <i className="fa-regular fa-clock" />

                                    {item.booking.time}
                                  </span>
                                )}
                              </div>
                            )}

                            {isFood &&
                              Array.isArray(item.options) &&
                              item.options.length > 0 && (
                                <div className="professional-option-list">
                                  {item.options.map((option, optionIndex) => (
                                    <span
                                      key={
                                        option._id ||
                                        option.modifierId ||
                                        option.id ||
                                        optionIndex
                                      }
                                    >
                                      {option.modifierName ||
                                        option.name ||
                                        "Option"}

                                      {safeNumber(option.price) > 0 && (
                                        <b>+{formatMoney(option.price)}</b>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}

                            <div className="professional-unit-price">
                              {formatMoney(unitPrice)} <small>each</small>
                            </div>
                          </div>

                          <div className="professional-cart-actions">
                            <div className="professional-quantity-control">
                              <button
                                type="button"
                                disabled={loading}
                                onClick={() => handleDecrease(item)}
                              >
                                {decrementLoading ? (
                                  <i className="fa-solid fa-spinner fa-spin" />
                                ) : (
                                  <i className="fa-solid fa-minus" />
                                )}
                              </button>

                              <span>{quantity}</span>

                              <button
                                type="button"
                                disabled={loading}
                                onClick={() => handleIncrease(item)}
                              >
                                {incrementLoading ? (
                                  <i className="fa-solid fa-spinner fa-spin" />
                                ) : (
                                  <i className="fa-solid fa-plus" />
                                )}
                              </button>
                            </div>

                            <strong className="professional-line-total">
                              {formatMoney(lineTotal)}
                            </strong>

                            <button
                              type="button"
                              disabled={loading}
                              className="professional-remove-btn"
                              onClick={() => handleRemove(item)}
                            >
                              {deleteLoading ? (
                                <i className="fa-solid fa-spinner fa-spin" />
                              ) : (
                                <i className="fa-regular fa-trash-can" />
                              )}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>

                <Link to="/" className="professional-back-link">
                  <i className="fa-solid fa-arrow-left" />
                  Continue Shopping
                </Link>
              </div>

              <div className="col-xl-4 col-lg-5">
                <div className="professional-summary-card">
                  <div className="professional-summary-header">
                    <span className="professional-summary-icon">
                      <i className="fa-solid fa-receipt" />
                    </span>

                    <div>
                      <h2>Order Summary</h2>

                      <p>Review your total</p>
                    </div>
                  </div>

                  <div className="professional-summary-body">
                    <div className="professional-summary-row">
                      <span>
                        <i className="fa-solid fa-box" />
                        Items
                      </span>

                      <strong>{cartCount}</strong>
                    </div>

                    {ticketItems.length > 0 && (
                      <div className="professional-summary-row">
                        <span>
                          <i className="fa-solid fa-ticket" />
                          Movie Tickets
                        </span>

                        <strong>{formatMoney(ticketTotal)}</strong>
                      </div>
                    )}

                    {productItems.length > 0 && (
                      <div className="professional-summary-row">
                        <span>
                          <i className="fa-solid fa-burger" />
                          Food & Drinks
                        </span>

                        <strong>{formatMoney(foodTotal)}</strong>
                      </div>
                    )}

                    <div className="professional-summary-divider" />

                    <div className="professional-summary-row">
                      <span>Subtotal</span>

                      <strong>{formatMoney(subtotal)}</strong>
                    </div>

                    <div className="professional-summary-row">
                      <span>Tax</span>

                      <strong>{formatMoney(taxAmount)}</strong>
                    </div>

                    {discountAmount > 0 && (
                      <div className="professional-summary-row discount">
                        <span>Discount</span>

                        <strong>-{formatMoney(discountAmount)}</strong>
                      </div>
                    )}

                    <div className="professional-summary-divider" />

                    <div className="professional-grand-total">
                      <div>
                        <span>Total</span>

                        <small>Including taxes</small>
                      </div>

                      <strong>{formatMoney(totalAmount)}</strong>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="professional-checkout-btn"
                    onClick={handleCheckout}
                  >
                    Proceed to Checkout
                    <i className="fa-solid fa-arrow-right" />
                  </button>

                  <div className="professional-secure-text">
                    <i className="fa-solid fa-lock" />
                    Secure checkout
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <VehicleDetailsModal
        isOpen={vehicleModalOpen}
        loading={onlineOrderLoading}
        onClose={() => {
          if (!onlineOrderLoading) {
            setVehicleModalOpen(false);
          }
        }}
        onSubmit={handleCreateOnlineOrder}
      />
    </main>
  );
};

export default Cart;
